import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Box,
  ButtonBase,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import WarningAmberIcon from "@mui/icons-material/WarningAmberOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import { nanoid } from "nanoid";
import type { LocalizedString, SchemeInfoSection } from "@shared/types/foyer";
import { localeMeta, setLocalized } from "@shared/i18n";
import { tokens } from "../../theme";
import { ItemFormSurface } from "../EditDrawer";

/**
 * Section editor with a single-language editing model (design 2a).
 *
 * The previous editor stacked one input per locale for every field, which
 * stopped scaling past two languages. Here the pane carries one "Content
 * language" bar: pick a locale and the whole form shows and edits that one
 * language. Each locale pill carries a status blob (translated / needs review
 * / empty) derived from the data, so the translation state of the entire
 * page reads at a glance.
 *
 * Shared by the Information directory and the Relatives pane - the only
 * difference is the optional per-section embed URL.
 */

// ── translation status ──────────────────────────────────────────────────────

export type LocaleStatus = "done" | "review" | "empty";

/**
 * Read one locale's text without the resolution fallback: an untranslated
 * field must render empty, not silently show the default-locale copy.
 *
 * A plain string is pre-i18n content that belongs to the default locale, so
 * it only ever fills the default-locale input.
 */
export function readAt(
  value: LocalizedString | undefined,
  locale: string,
  defaultLocale: string
): string {
  if (!value) return "";
  if (typeof value === "string") return locale === defaultLocale ? value : "";
  return value[locale] ?? "";
}

/** Every translatable field across the sections, flattened. */
function collectFields(sections: SchemeInfoSection[]): LocalizedString[] {
  const out: LocalizedString[] = [];
  for (const sec of sections) {
    out.push(sec.section);
    for (const it of sec.items) {
      out.push(it.label);
      out.push(it.value);
    }
  }
  return out;
}

/**
 * Derive a locale's status from the data rather than storing a flag, so the
 * blobs stay truthful automatically.
 *
 * "Required" means the default locale has copy for that field - there is
 * nothing to translate where the source itself is blank. The default locale
 * is therefore never reported as untranslated.
 */
export function localeStatus(
  sections: SchemeInfoSection[],
  locale: string,
  defaultLocale: string
): LocaleStatus {
  if (locale === defaultLocale) return "done";
  const fields = collectFields(sections);
  let required = 0;
  let filled = 0;
  for (const f of fields) {
    if (readAt(f, defaultLocale, defaultLocale).trim().length === 0) continue;
    required++;
    if (readAt(f, locale, defaultLocale).trim().length > 0) filled++;
  }
  if (required === 0 || filled === required) return "done";
  return filled === 0 ? "empty" : "review";
}

const BLOB_COLOR: Record<LocaleStatus, string> = {
  done: tokens.success,
  review: tokens.warning,
  empty: tokens.inkFaint,
};

// ── content language bar ────────────────────────────────────────────────────

export function ContentLanguageBar({
  locales,
  defaultLocale,
  active,
  statusOf,
  onSelect,
}: {
  locales: string[];
  defaultLocale: string;
  active: string;
  statusOf: (locale: string) => LocaleStatus;
  onSelect: (locale: string) => void;
}) {
  const { t } = useTranslation("templates");
  const doneCount = locales.filter((l) => statusOf(l) === "done").length;
  return (
    <Stack spacing={1.125}>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
      >
        <Typography
          variant="meta"
          sx={{ fontWeight: "fontWeightSemibold", color: tokens.inkMuted }}
        >
          {t("localizedSectionsEditor.contentLanguage")}
        </Typography>
        <Typography variant="micro" sx={{ color: tokens.inkFaint }}>
          {t("localizedSectionsEditor.localeSummary", {
            count: locales.length,
            done: doneCount,
          })}
        </Typography>
      </Stack>
      <Box
        role="group"
        aria-label={t("localizedSectionsEditor.contentLanguage")}
        sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
      >
        {locales.map((code) => {
          const m = localeMeta(code);
          const isDefault = code === defaultLocale;
          const selected = code === active;
          const status = statusOf(code);
          return (
            <ButtonBase
              key={code}
              onClick={() => onSelect(code)}
              aria-pressed={selected}
              aria-label={t(
                isDefault
                  ? "localizedSectionsEditor.localeAriaDefault"
                  : "localizedSectionsEditor.localeAria",
                {
                  name: m.native,
                  status: t(`localizedSectionsEditor.status.${status}`),
                }
              )}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.875,
                px: 1.5,
                py: 0.875,
                borderRadius: "9px",
                typography: "meta",
                fontWeight: "fontWeightMedium",
                border: "1px solid",
                transition: "background-color .12s, border-color .12s",
                ...(selected
                  ? {
                      bgcolor: tokens.ink,
                      color: tokens.surface,
                      borderColor: tokens.ink,
                    }
                  : {
                      bgcolor: tokens.surface,
                      color: tokens.ink,
                      borderColor: tokens.divider,
                      "&:hover": { borderColor: tokens.inkFaint },
                    }),
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 7,
                  height: 7,
                  flex: "none",
                  borderRadius: "50%",
                  bgcolor: BLOB_COLOR[status],
                  opacity: status === "empty" ? 0.55 : 1,
                }}
              />
              {m.native}
              {isDefault && (
                <Box
                  component="span"
                  sx={{
                    fontSize: 9.5,
                    fontWeight: "fontWeightBold",
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                    opacity: 0.65,
                  }}
                >
                  {t("localizedSectionsEditor.defaultBadge")}
                </Box>
              )}
            </ButtonBase>
          );
        })}
      </Box>
    </Stack>
  );
}

// ── single-locale field ─────────────────────────────────────────────────────

/**
 * One input bound to the active locale. When the field has no translation
 * for a non-default locale it renders in the muted "Not translated" style
 * (dashed border, italic placeholder) so gaps are visible without reading
 * every field.
 */
export function LocaleField({
  value,
  locale,
  defaultLocale,
  onChange,
  placeholder,
  ariaLabel,
  testId,
}: {
  value: LocalizedString | undefined;
  locale: string;
  defaultLocale: string;
  onChange: (next: LocalizedString) => void;
  placeholder?: string;
  ariaLabel: string;
  testId?: string;
}) {
  const { t } = useTranslation("templates");
  const text = readAt(value, locale, defaultLocale);
  const untranslated = text.length === 0 && locale !== defaultLocale;
  return (
    <TextField
      fullWidth
      size="small"
      value={text}
      onChange={(e) =>
        onChange(setLocalized(value, locale, defaultLocale, e.target.value))
      }
      placeholder={
        untranslated ? t("localizedSectionsEditor.notTranslated") : placeholder
      }
      inputProps={{
        "aria-label": ariaLabel,
        ...(testId ? { "data-testid": `${testId}-${locale}` } : {}),
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          bgcolor: untranslated ? tokens.rowAlt : tokens.surface,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderStyle: untranslated ? "dashed" : "solid",
        },
        "& input::placeholder": untranslated
          ? { fontStyle: "italic", opacity: 1, color: tokens.inkFaint }
          : {},
      }}
    />
  );
}

// ── section card ────────────────────────────────────────────────────────────

const CAPTION_SX = {
  fontSize: 11,
  fontWeight: "fontWeightSemibold",
  letterSpacing: ".05em",
  textTransform: "uppercase" as const,
  color: tokens.inkFaint,
};

const ROW_GRID = "1fr 1.35fr 34px";

const EMBED_URL_PLACEHOLDER = "https://…/embed?token={integrationId}";

/** Does `section` have default-locale copy that `locale` lacks? */
function missingIn(
  section: SchemeInfoSection,
  locale: string,
  defaultLocale: string
): boolean {
  return (
    locale !== defaultLocale &&
    [
      section.section,
      ...section.items.flatMap((it) => [it.label, it.value]),
    ].some(
      (f) =>
        readAt(f, defaultLocale, defaultLocale).trim().length > 0 &&
        readAt(f, locale, defaultLocale).trim().length === 0
    )
  );
}

/** One section in the pane's list: its title and row count, reorder, edit and
 *  delete. The fields themselves are edited in {@link SectionForm}. */
function SectionRow({
  index,
  section,
  locale,
  defaultLocale,
  onEdit,
  onUp,
  onDown,
  onDelete,
}: {
  index: number;
  section: SchemeInfoSection;
  locale: string;
  defaultLocale: string;
  onEdit: () => void;
  onUp: () => void;
  onDown: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation("templates");
  const missing = missingIn(section, locale, defaultLocale);
  const title =
    readAt(section.section, locale, defaultLocale).trim() ||
    readAt(section.section, defaultLocale, defaultLocale).trim() ||
    t("localizedSectionsEditor.untitledSection");
  return (
    <Box
      data-testid={`section-row-${section.id}`}
      sx={{
        bgcolor: tokens.surface,
        border: "1px solid",
        borderColor: tokens.divider,
        borderRadius: "14px",
        boxShadow: tokens.shadowCard,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2.25,
          py: 1.75,
          borderBottom: "1px solid",
          borderColor: tokens.divider,
          bgcolor: tokens.rowAlt,
        }}
      >
        <Typography
          variant="micro"
          sx={{ typography: "eyebrow", color: tokens.inkFaint }}
        >
          {t("localizedSectionsEditor.sectionNumber", { n: index + 1 })}
        </Typography>
        <Stack direction="row" spacing={0.75}>
          <Tooltip title={t("localizedSectionsEditor.editSection")}>
            <IconButton
              size="small"
              aria-label={t("localizedSectionsEditor.editSection")}
              onClick={onEdit}
            >
              <EditIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("localizedSectionsEditor.moveUp")}>
            <IconButton
              size="small"
              aria-label={t("localizedSectionsEditor.moveUp")}
              onClick={onUp}
            >
              <ArrowUpwardIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("localizedSectionsEditor.moveDown")}>
            <IconButton
              size="small"
              aria-label={t("localizedSectionsEditor.moveDown")}
              onClick={onDown}
            >
              <ArrowDownwardIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("localizedSectionsEditor.deleteSection")}>
            <IconButton
              size="small"
              color="error"
              aria-label={t("localizedSectionsEditor.deleteSection")}
              onClick={onDelete}
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        role="button"
        tabIndex={0}
        onClick={onEdit}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onEdit();
          }
        }}
        sx={{
          px: 2.25,
          py: 1.75,
          cursor: "pointer",
          "&:hover": { bgcolor: tokens.rowAlt },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
          >
            {title}
          </Typography>
          <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
            {section.embedUrl
              ? t("localizedSectionsEditor.embeddedPage")
              : t("localizedSectionsEditor.rowCount", {
                  count: section.items.length,
                })}
          </Typography>
        </Box>
        {missing && (
          <Tooltip
            title={t("localizedSectionsEditor.missingTooltip", {
              language: localeMeta(locale).native,
            })}
          >
            <WarningAmberIcon
              fontSize="small"
              aria-label={t("localizedSectionsEditor.notFullyTranslated")}
              sx={{ color: tokens.warning }}
            />
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

/**
 * The fields of ONE section - title, optional embed URL and its label / value
 * rows - in the active locale. Rendered inside the Add modal and the Edit
 * drawer; the rows are part of the section, so they are added and removed
 * right here rather than in a surface of their own.
 */
function SectionForm({
  section,
  locale,
  defaultLocale,
  showEmbedUrl,
  onPatch,
}: {
  section: SchemeInfoSection;
  locale: string;
  defaultLocale: string;
  showEmbedUrl: boolean;
  onPatch: (patch: Partial<SchemeInfoSection>) => void;
}) {
  const { t } = useTranslation("templates");
  const missing = missingIn(section, locale, defaultLocale);

  const setItem = (
    j: number,
    patch: Partial<SchemeInfoSection["items"][number]>
  ) =>
    onPatch({
      items: section.items.map((x, k) => (k === j ? { ...x, ...patch } : x)),
    });

  return (
    <Stack spacing={2.25}>
      {missing && (
        <Stack
          direction="row"
          spacing={1.25}
          sx={{
            alignItems: "flex-start",
            px: 1.625,
            py: 1.375,
            bgcolor: tokens.warningTint,
            border: "1px solid",
            borderColor: tokens.warning,
            borderRadius: "9px",
          }}
        >
          <WarningAmberIcon
            fontSize="small"
            sx={{ color: tokens.warning, flex: "none", mt: "1px" }}
          />
          <Typography
            variant="meta"
            sx={{ lineHeight: 1.5, color: tokens.ink }}
          >
            <Trans
              t={t}
              i18nKey="localizedSectionsEditor.missingBanner"
              values={{
                language: localeMeta(locale).native,
                defaultLanguage: localeMeta(defaultLocale).native,
              }}
              components={{ strong: <strong /> }}
            />
          </Typography>
        </Stack>
      )}

      <Stack spacing={0.75}>
        <Typography sx={CAPTION_SX}>
          {t("localizedSectionsEditor.sectionTitle")}
        </Typography>
        <LocaleField
          value={section.section}
          locale={locale}
          defaultLocale={defaultLocale}
          ariaLabel={t("localizedSectionsEditor.sectionTitle")}
          testId="section-title"
          onChange={(v) => onPatch({ section: v })}
        />
        <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
          {t("localizedSectionsEditor.sectionTitleHelp")}
        </Typography>
      </Stack>

      {showEmbedUrl && (
        <Stack spacing={0.75}>
          <Typography sx={CAPTION_SX}>
            {t("localizedSectionsEditor.embedUrl")}{" "}
            <Box
              component="span"
              sx={{
                textTransform: "none",
                letterSpacing: 0,
                fontWeight: "fontWeightMedium",
                color: tokens.inkFaint,
              }}
            >
              {t("localizedSectionsEditor.optional")}
            </Box>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={EMBED_URL_PLACEHOLDER}
            value={section.embedUrl ?? ""}
            inputProps={{
              "aria-label": t("localizedSectionsEditor.embedUrlAria"),
            }}
            onChange={(e) =>
              onPatch({ embedUrl: e.target.value.trim() || undefined })
            }
          />
          <Typography
            variant="meta"
            sx={{ lineHeight: 1.5, color: tokens.inkFaint }}
          >
            <Trans
              t={t}
              i18nKey="localizedSectionsEditor.embedUrlHelp"
              components={{
                code: (
                  <Box
                    component="code"
                    sx={{
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, monospace",
                      typography: "micro",
                      bgcolor: tokens.rowAlt,
                      color: tokens.inkMuted,
                      px: 0.625,
                      py: "1px",
                      borderRadius: "4px",
                    }}
                  />
                ),
              }}
            />
          </Typography>
        </Stack>
      )}

      <Box sx={{ height: "1px", bgcolor: tokens.divider }} />

      <Stack spacing={1.25}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography
              variant="bodySm"
              sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}
            >
              {t("localizedSectionsEditor.rows")}
            </Typography>
            <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
              {t("localizedSectionsEditor.itemCount", {
                count: section.items.length,
              })}
            </Typography>
          </Stack>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() =>
              onPatch({ items: [...section.items, { label: "", value: "" }] })
            }
            sx={{
              color: tokens.brand,
              bgcolor: tokens.brandTintSoft,
              border: "1px solid",
              borderColor: tokens.brandTint,
              "&:hover": { bgcolor: tokens.brandTint },
            }}
          >
            {t("localizedSectionsEditor.addRow")}
          </Button>
        </Stack>

        {section.items.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: ROW_GRID,
              gap: 1.25,
              px: "2px",
            }}
          >
            <Typography sx={CAPTION_SX}>
              {t("localizedSectionsEditor.label")}
            </Typography>
            <Typography sx={CAPTION_SX}>
              {t("localizedSectionsEditor.value")}
            </Typography>
            <Box />
          </Box>
        )}

        {section.items.map((it, j) => (
          <Box
            key={j}
            sx={{
              display: "grid",
              gridTemplateColumns: ROW_GRID,
              gap: 1.25,
              alignItems: "center",
            }}
          >
            <LocaleField
              value={it.label}
              locale={locale}
              defaultLocale={defaultLocale}
              ariaLabel={t("localizedSectionsEditor.rowLabel", { n: j + 1 })}
              onChange={(v) => setItem(j, { label: v })}
            />
            <LocaleField
              value={it.value}
              locale={locale}
              defaultLocale={defaultLocale}
              ariaLabel={t("localizedSectionsEditor.rowValue", { n: j + 1 })}
              onChange={(v) => setItem(j, { value: v })}
            />
            <IconButton
              size="small"
              color="error"
              aria-label={t("localizedSectionsEditor.deleteRow", { n: j + 1 })}
              onClick={() =>
                onPatch({ items: section.items.filter((_, k) => k !== j) })
              }
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}

// ── pane ────────────────────────────────────────────────────────────────────

/** Move helper - returns a new array with item i shifted by `dir` (±1). */
function move<T>(arr: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = arr.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

export default function LocalizedSectionsEditor({
  sections,
  onChange,
  supportedLocales,
  defaultLocale,
  showEmbedUrl = false,
}: {
  sections: SchemeInfoSection[];
  onChange: (next: SchemeInfoSection[]) => void;
  supportedLocales: string[];
  defaultLocale: string;
  /** Information sections carry a per-section embed URL; Relatives doesn't. */
  showEmbedUrl?: boolean;
}) {
  const { t } = useTranslation("templates");
  const locales = useMemo(() => {
    const list =
      supportedLocales.length > 0 ? supportedLocales : [defaultLocale];
    // Default first, then the rest in the premise's configured order.
    return [defaultLocale, ...list.filter((l) => l !== defaultLocale)];
  }, [supportedLocales, defaultLocale]);

  const [activeLocale, setActiveLocale] = useState(defaultLocale);
  // The premise's locale set is editable elsewhere; fall back to the default
  // rather than editing a locale that is no longer supported.
  useEffect(() => {
    if (!locales.includes(activeLocale)) setActiveLocale(defaultLocale);
  }, [locales, activeLocale, defaultLocale]);
  const locale = locales.includes(activeLocale) ? activeLocale : defaultLocale;

  const statusOf = (code: string) =>
    localeStatus(sections, code, defaultLocale);

  // Which section the modal / drawer is on: "new" = adding one.
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const editingSection =
    editing === "new"
      ? { id: nanoid(6), section: "New section", items: [] }
      : sections.find((s) => s.id === editing);

  return (
    <Stack spacing={2.25}>
      <ContentLanguageBar
        locales={locales}
        defaultLocale={defaultLocale}
        active={locale}
        statusOf={statusOf}
        onSelect={setActiveLocale}
      />
      {sections.map((sec, i) => (
        <SectionRow
          key={sec.id}
          index={i}
          section={sec}
          locale={locale}
          defaultLocale={defaultLocale}
          onEdit={() => setEditing(sec.id)}
          onUp={() => onChange(move(sections, i, -1))}
          onDown={() => onChange(move(sections, i, 1))}
          onDelete={() => onChange(sections.filter((_, idx) => idx !== i))}
        />
      ))}
      <Button
        startIcon={<AddIcon />}
        onClick={() => setEditing("new")}
        sx={{ alignSelf: "flex-start" }}
      >
        {t("localizedSectionsEditor.addSection")}
      </Button>
      {editing !== null && editingSection && (
        <SectionSurface
          key={editing === "new" ? "new" : editing}
          mode={editing === "new" ? "create" : "edit"}
          initial={editingSection}
          sections={sections}
          locales={locales}
          locale={locale}
          defaultLocale={defaultLocale}
          showEmbedUrl={showEmbedUrl}
          onLocale={setActiveLocale}
          onClose={() => setEditing(null)}
          onSave={(next) => {
            onChange(
              editing === "new"
                ? [...sections, next]
                : sections.map((s) => (s.id === next.id ? next : s))
            );
            setEditing(null);
          }}
        />
      )}
    </Stack>
  );
}

/**
 * Add (modal) / edit (right drawer) one section. It edits a local copy: Add /
 * Apply hands it to `onSave` - which writes it into the draft the page saves
 * later - once the surface has closed; Cancel throws it away.
 */
function SectionSurface({
  mode,
  initial,
  sections,
  locales,
  locale,
  defaultLocale,
  showEmbedUrl,
  onLocale,
  onClose,
  onSave,
}: {
  mode: "create" | "edit";
  initial: SchemeInfoSection;
  /** Every section, so the language bar's status counts the edit in progress. */
  sections: SchemeInfoSection[];
  locales: string[];
  locale: string;
  defaultLocale: string;
  showEmbedUrl: boolean;
  onLocale: (locale: string) => void;
  onClose: () => void;
  onSave: (next: SchemeInfoSection) => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const [draft, setDraft] = useState<SchemeInfoSection>(initial);
  const withDraft =
    mode === "create"
      ? [...sections, draft]
      : sections.map((s) => (s.id === draft.id ? draft : s));
  return (
    <ItemFormSurface
      mode={mode}
      onClose={onClose}
      title={
        mode === "create"
          ? t("localizedSectionsEditor.addSection")
          : t("localizedSectionsEditor.editSection")
      }
      maxWidth="md"
      drawerWidth={720}
      data-testid="section-form"
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => dismiss(() => onSave(draft))}
            data-testid="section-save"
          >
            {mode === "create"
              ? t("common:buttons.add")
              : t("common:buttons.apply")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2.25} sx={{ pt: 1 }}>
        {locales.length > 1 && (
          <ContentLanguageBar
            locales={locales}
            defaultLocale={defaultLocale}
            active={locale}
            statusOf={(code) => localeStatus(withDraft, code, defaultLocale)}
            onSelect={onLocale}
          />
        )}
        <SectionForm
          section={draft}
          locale={locale}
          defaultLocale={defaultLocale}
          showEmbedUrl={showEmbedUrl}
          onPatch={(part) => setDraft((d) => ({ ...d, ...part }))}
        />
      </Stack>
    </ItemFormSurface>
  );
}
